'use client';

import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TitleBar from '../layout/TitleBar';
import GenericFormModal from '../form/GenericFormModal';
import { GENERIC_FORM_FIELD_TYPE } from '../form/GenericForm';
import GenericActionPicker, { GENERIC_ICON_NAME } from '../form/GenericActionPicker';
import {
  cancelIaPurchaseOrder,
  createIaPurchaseOrder,
  fetchIaInventorySettings,
  searchIaAlertUsers,
  queryIaBuyingSuggestions,
  queryIaReplenishmentPolicies,
  saveIaReplenishmentPolicy,
  updateIaInventorySettings,
  updateIaPurchaseOrder,
  useIaPurchaseOrdersList,
  queryIaVendors,
  type IaPurchaseOrder,
  type IaVendor,
} from '@/hooks/useInventoryAuditorPurchasing';
import { useInventoryAuditorLocationsList } from '@/hooks/useInventoryAuditorLocation';
import { useNotification } from '@/contexts/NotificationContext';
import BuyingSuggestionsPanel, { type BuyingSuggestionRow } from '../purchasing/BuyingSuggestionsPanel';
import ReplenishmentPolicyPanel, { type ReplenishmentPolicyRow } from '../purchasing/ReplenishmentPolicyPanel';
import { searchInventoryAuditorCatalogItems } from '../purchasing/catalogSearch';
import InventoryAlertSettingsCard from '../purchasing/InventoryAlertSettingsCard';

function statusChip(status: string) {
  const map: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
    draft: 'default',
    submitted: 'info',
    partially_received: 'warning',
    received: 'success',
    cancelled: 'error',
  };
  return (
    <Chip label={status.replace(/_/g, ' ')} size="small" color={map[status] ?? 'default'} variant="outlined" />
  );
}

const InventoryAuditorPurchaseOrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const { data: locations = [] } = useInventoryAuditorLocationsList();
  const [vendors, setVendors] = useState<IaVendor[]>([]);
  const [search, setSearch] = useState('');
  const [showCancelled, setShowCancelled] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<IaPurchaseOrder | null>(null);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });
  const [sorting, setSorting] = useState([] as { id: string; desc: boolean }[]);
  const [tab, setTab] = useState(0);

  const { data: list = [], isLoading, refetch } = useIaPurchaseOrdersList(search, showCancelled);

  React.useEffect(() => {
    void queryIaVendors().then(setVendors);
  }, []);

  const headerFieldGroups = useMemo(
    () => [
      {
        groupName: 'Purchase order',
        fields: [
          {
            label: 'Vendor',
            name: 'vendorId',
            type: GENERIC_FORM_FIELD_TYPE.SINGLE_SELECT,
            required: true,
            columns: 12,
            options: vendors.map((v) => ({ label: v.name, value: v.id })),
          },
          {
            label: 'Receive at location',
            name: 'receiveLocationId',
            type: GENERIC_FORM_FIELD_TYPE.SINGLE_SELECT,
            required: true,
            columns: 12,
            options: locations.map((l: { id: number; name?: string }) => ({
              label: l.name ?? `Location ${l.id}`,
              value: l.id,
            })),
          },
          { label: 'PO number', name: 'poNumber', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: true, columns: 12 },
          { label: 'Notes', name: 'notes', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 12 },
        ],
      },
    ],
    [vendors, locations],
  );

  const openCreate = useCallback(() => {
    setEditing(null);
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((po: IaPurchaseOrder) => {
    setEditing(po);
    setModalOpen(true);
  }, []);

  const handleCancel = useCallback(
    async (po: IaPurchaseOrder) => {
      if (!window.confirm(`Cancel purchase order "${po.poNumber}"?`)) return;
      try {
        await cancelIaPurchaseOrder(po.id);
        showSuccess('Purchase order cancelled');
        await refetch();
      } catch (e: unknown) {
        showError(e instanceof Error ? e.message : 'Failed to cancel');
      }
    },
    [refetch, showError, showSuccess],
  );

  const columns = useMemo<MRT_ColumnDef<IaPurchaseOrder>[]>(
    () => [
      { accessorKey: 'poNumber', header: 'PO #', size: 140 },
      {
        accessorKey: 'vendor.name',
        header: 'Vendor',
        Cell: ({ row }) => row.original.vendor?.name ?? '—',
      },
      {
        accessorKey: 'receiveLocation.name',
        header: 'Receive location',
        Cell: ({ row }) => row.original.receiveLocation?.name ?? '—',
      },
      {
        accessorKey: 'status',
        header: 'Status',
        Cell: ({ row }) => statusChip(row.original.status),
      },
      {
        id: 'actions',
        header: 'Actions',
        Cell: ({ row }) => {
          const po = row.original;
          const canEdit = po.status === 'draft';
          const canCancel = po.status !== 'cancelled' && po.status !== 'received';
          return (
            <GenericActionPicker
              row={po}
              actions={[
                {
                  icon: GENERIC_ICON_NAME.VIEW,
                  label: 'Open',
                  onClick: (r) => navigate(`/app/inventory-auditor/purchasing/purchase-orders/${r.id}`),
                },
                ...(canEdit
                  ? [{ icon: GENERIC_ICON_NAME.EDIT, label: 'Edit header', onClick: (r: IaPurchaseOrder) => openEdit(r) }]
                  : []),
                ...(canCancel
                  ? [{ icon: GENERIC_ICON_NAME.BLOCK, label: 'Cancel', onClick: (r: IaPurchaseOrder) => void handleCancel(r) }]
                  : []),
              ]}
            />
          );
        },
      },
    ],
    [handleCancel, navigate, openEdit],
  );

  return (
    <>
      <GenericFormModal
        visible={modalOpen}
        onDismiss={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSubmit={async (values: Record<string, unknown>) => {
          const vendorId = Number(values.vendorId);
          const receiveLocationId = Number(values.receiveLocationId);
          const poNumber = String(values.poNumber ?? '').trim();
          if (!vendorId || !receiveLocationId || !poNumber) {
            showError('Vendor, location, and PO number are required');
            return;
          }
          try {
            if (editing) {
              await updateIaPurchaseOrder({
                id: editing.id,
                vendorId,
                receiveLocationId,
                poNumber,
                notes: String(values.notes ?? '').trim() || null,
              });
              showSuccess('Purchase order updated');
              setModalOpen(false);
              setEditing(null);
              await refetch();
            } else {
              const po = await createIaPurchaseOrder({
                vendorId,
                receiveLocationId,
                poNumber,
                notes: String(values.notes ?? '').trim() || undefined,
                lines: [],
              });
              showSuccess('Purchase order created');
              setModalOpen(false);
              navigate(`/app/inventory-auditor/purchasing/purchase-orders/${po.id}`);
            }
          } catch (e: unknown) {
            showError(e instanceof Error ? e.message : 'Failed to save');
          }
        }}
        initialValues={
          editing
            ? {
                vendorId: editing.vendor?.id ?? '',
                receiveLocationId: editing.receiveLocation?.id ?? '',
                poNumber: editing.poNumber ?? '',
                notes: editing.notes ?? '',
              }
            : { vendorId: '', receiveLocationId: '', poNumber: `PO-${Date.now()}`, notes: '' }
        }
        fieldGroups={headerFieldGroups}
        title={editing ? 'Edit purchase order' : 'New purchase order'}
      />

      <TitleBar
        breadcrumbs={[{ label: 'Home', href: '/app/dashboard' }, { label: 'Purchase orders' }]}
        title="Purchase orders"
        rightContent={
          <Button variant="contained" onClick={openCreate} sx={{ textTransform: 'none' }}>
            + New purchase order
          </Button>
        }
      />

      <Box sx={{ p: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="Purchase orders" />
          <Tab label="Buying suggestions" />
          <Tab label="Replenishment policy" />
        </Tabs>

        {tab === 1 && (
          <BuyingSuggestionsPanel
            businessUnit="ia"
            locations={locations ?? []}
            vendors={vendors}
            fetchSuggestions={async (opts) => (await queryIaBuyingSuggestions(opts)) as BuyingSuggestionRow[]}
            createDraftPo={createIaPurchaseOrder}
            poDetailPath={(id) => `/app/inventory-auditor/purchasing/purchase-orders/${id}`}
          />
        )}

        {tab === 2 && (
          <ReplenishmentPolicyPanel
            locations={locations ?? []}
            vendors={vendors}
            queryPolicies={async (locationId) =>
              (await queryIaReplenishmentPolicies(locationId)) as ReplenishmentPolicyRow[]
            }
            savePolicy={saveIaReplenishmentPolicy}
            searchCatalog={searchInventoryAuditorCatalogItems}
          />
        )}

        {tab === 0 && (
        <>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search PO # or vendor…"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 300 }}
          />
          <FormControlLabel
            control={
              <Switch checked={showCancelled} onChange={(e) => setShowCancelled(e.target.checked)} size="small" />
            }
            label="Show cancelled"
          />
        </Box>

        <InventoryAlertSettingsCard
          fetchSettings={fetchIaInventorySettings}
          saveSettings={updateIaInventorySettings}
          searchUsers={searchIaAlertUsers}
          showCostingMethod
        />

        <MaterialReactTable
          columns={columns}
          data={list}
          enableGlobalFilter={false}
          onPaginationChange={setPagination}
          onSortingChange={setSorting}
          rowCount={list.length}
          state={{ isLoading, pagination, sorting }}
          initialState={{ density: 'compact' }}
          muiTableBodyRowProps={({ row }) => ({
            onClick: () => navigate(`/app/inventory-auditor/purchasing/purchase-orders/${row.original.id}`),
            sx: { cursor: 'pointer' },
          })}
        />
        </>
        )}
      </Box>
    </>
  );
};

export default InventoryAuditorPurchaseOrdersPage;
